import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCompaniesContext } from 'company/context'
import { ObserverStoreChooserScreenProps } from 'home/stack/types'
import { isEmpty } from 'lodash'
import { useAddEditUserContext } from 'management_team/context'
import { useManagementTeamActions } from 'management_team/management_actions_context'
import { useCompanyStores } from 'management_team/queries/stores'
import { filterStores } from 'management_team/util'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { ObserverStoreChooserView } from './views'

export function ObserverStoreChooserScreen({
  navigation,
  route,
}: ObserverStoreChooserScreenProps) {
  const { userFields, setUserFields, type } = useAddEditUserContext()
  const { onEditUser } = useManagementTeamActions()
  const { activeCompany } = useCompaniesContext()
  const { handleNavigation } = useRoutingContext()
  const companyStoresQuery = useCompanyStores(activeCompany?.id)

  const { theme } = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>(
    userFields.storeIds ?? []
  )
  const filteredStores = useMemo(
    () => filterStores(searchTerm, companyStoresQuery.data),
    [companyStoresQuery.data, searchTerm]
  )

  const selectedStores = useMemo(
    () =>
      filteredStores?.filter(a => selectedStoreIds?.includes(Number(a.id))) ??
      [],
    [selectedStoreIds, filteredStores]
  )

  const unselectedStores = useMemo(
    () =>
      filteredStores?.filter(a => !selectedStoreIds?.includes(Number(a.id))) ??
      [],
    [selectedStoreIds, filteredStores]
  )

  const sortedStoreList = useMemo(() => {
    return selectedStores.concat(unselectedStores)
  }, [selectedStores, unselectedStores])

  const onSubmit = useCallback(async () => {
    setUserFields({
      ...userFields,
      storeIds: selectedStoreIds,
    })
    if (type === 'add' || type === 'editUser') {
      handleNavigation({ routeName: 'add_edit_user' })
    } else if (type === 'editStores') {
      onEditUser({
        ...userFields,
        storeIds: selectedStoreIds,
      })

      navigation.goBack()
    }
  }, [
    setUserFields,
    userFields,
    selectedStoreIds,
    type,
    handleNavigation,
    onEditUser,
    navigation,
  ])

  const canSubmit = useCallback(() => {
    return !isEmpty(selectedStoreIds)
  }, [selectedStoreIds])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      theme,
      title: t('select'),
      onPress: onSubmit,
      position: 'right',
      useIcon: false,
      disabled: !canSubmit(),
    })
  }, [canSubmit, navigation, onSubmit, theme, type])

  return (
    <StatusBoundary
      isLoading={companyStoresQuery.isLoading}
      error={undefined}
    >
      <ObserverStoreChooserView
        stores={sortedStoreList}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedStoreIds={selectedStoreIds}
        setSelectedStoreIds={setSelectedStoreIds}
      />
    </StatusBoundary>
  )
}
